/**
 * AIAssistant — I2Speed Cortex AI Chat Interface
 *
 * Features:
 *  - Streaming word-by-word response display
 *  - Conversation history sidebar
 *  - Role-aware quick action chips
 *  - Markdown rendering (bold, tables, bullet lists)
 *  - Thumbs up/down feedback per message
 *  - Token usage display (HR only)
 *  - "AI not configured" graceful state
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Button, Form, Spinner, Badge, Alert } from 'react-bootstrap';
import {
    Send, Bot, User, Plus, Trash2, Zap,
    ThumbsUp, ThumbsDown, Settings, Clock,
    ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

// ── Markdown renderer (no extra dependency) ───────────────────────────────────
const renderMarkdown = (text) => {
    if (!text) return '';
    let html = text
        // Code blocks
        .replace(/```([\s\S]*?)```/g, '<pre class="ai-code"><code>$1</code></pre>')
        // Inline code
        .replace(/`([^`]+)`/g, '<code class="ai-inline-code">$1</code>')
        // Bold
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        // Italic
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        // H3
        .replace(/^### (.+)$/gm, '<h6 class="fw-bold mt-3 mb-1">$1</h6>')
        // H2
        .replace(/^## (.+)$/gm, '<h5 class="fw-bold mt-3 mb-2">$1</h5>')
        // H1
        .replace(/^# (.+)$/gm, '<h4 class="fw-bold mt-3 mb-2">$1</h4>')
        // Bullet lists
        .replace(/^[-•] (.+)$/gm, '<li class="mb-1">$1</li>')
        // Numbered lists
        .replace(/^\d+\. (.+)$/gm, '<li class="mb-1">$1</li>')
        // Horizontal rule
        .replace(/^---$/gm, '<hr class="my-2"/>')
        // Line breaks (double newline → paragraph, single → br)
        .replace(/\n\n/g, '</p><p class="mb-2">')
        .replace(/\n/g, '<br/>');

    // Wrap consecutive <li> in <ul>
    html = html.replace(/(<li[\s\S]*?<\/li>(\s*<br\/>)?)+/g, (match) => {
        const items = match.replace(/<br\/>/g, '');
        return `<ul class="ps-3 mb-2">${items}</ul>`;
    });

    return `<p class="mb-2">${html}</p>`;
};

const MessageContent = ({ content }) => (
    <div
        className="ai-message-content"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
    />
);

// ── Time formatter ────────────────────────────────────────────────────────────
const fmtTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' });
};

const fmtDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Today';
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-IN', { day:'numeric', month:'short' });
};

// ════════════════════════════════════════════════════════════════════════════
const AIAssistant = () => {
    const { user }   = useAuth();
    const isHR       = ['super-admin','admin','hr-manager','account-manager']
                        .includes(user?.role);
    const isMgr      = user?.role === 'reporting-manager';

    // ── State ─────────────────────────────────────────────────────────────
    const [conversations, setConversations] = useState([]);
    const [activeConvId,  setActiveConvId]  = useState(null);
    const [messages,      setMessages]      = useState([]);
    const [quickActions,  setQuickActions]  = useState([]);
    const [input,         setInput]         = useState('');
    const [isStreaming,   setIsStreaming]    = useState(false);
    const [streamText,    setStreamText]    = useState('');
    const [notConfigured, setNotConfigured] = useState(false);
    const [showSidebar,   setShowSidebar]   = useState(true);
    const [usage,         setUsage]         = useState(null);

    const bottomRef = useRef(null);
    const inputRef  = useRef(null);

    // ── Auto-scroll ───────────────────────────────────────────────────────
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, streamText]);

    // ── Load conversations + quick actions on mount ───────────────────────
    useEffect(() => {
        axiosInstance.get('/ai/conversations')
            .then(r => setConversations(r.data.data || []))
            .catch(() => {});

        axiosInstance.get('/ai/quick-actions')
            .then(r => setQuickActions(r.data.data || []))
            .catch(() => {});

        if (isHR) {
            axiosInstance.get('/ai/usage')
                .then(r => setUsage(r.data.data))
                .catch(() => {});
        }
    }, [isHR]);

    // ── Load conversation history ─────────────────────────────────────────
    const loadConversation = useCallback(async (id) => {
        setActiveConvId(id);
        setMessages([]);
        try {
            const res = await axiosInstance.get(`/ai/conversations/${id}`);
            const msgs = res.data.data?.messages || [];
            setMessages(msgs.map(m => ({
                role:      m.role,
                content:   m.content,
                timestamp: m.created_at,
                id:        m.id,
            })));
        } catch { toast.error('Failed to load conversation'); }
    }, []);

    // ── Start new conversation ────────────────────────────────────────────
    const newConversation = () => {
        setActiveConvId(null);
        setMessages([]);
        setInput('');
        inputRef.current?.focus();
    };

    // ── Send message ──────────────────────────────────────────────────────
    const sendMessage = useCallback(async (text) => {
        const msg = (text || input).trim();
        if (!msg || isStreaming) return;

        setInput('');
        setIsStreaming(true);
        setStreamText('');

        // Optimistically add user message
        const userMsg = { role:'user', content:msg, timestamp: new Date().toISOString(), id: Date.now() };
        setMessages(prev => [...prev, userMsg]);

        try {
            // Use streaming endpoint for live word-by-word display
            const response = await fetch(
                `${(process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/api\/v1$/, '')}/api/v1/ai/chat/stream`,
                {
                    method:  'POST',
                    headers: {
                        'Content-Type':  'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    },
                    body: JSON.stringify({
                        message:         msg,
                        conversation_id: activeConvId,
                    }),
                }
            );

            if (!response.ok) {
                const err = await response.json();
                if (response.status === 503) setNotConfigured(true);
                throw new Error(err.message || 'AI service error');
            }

            const reader   = response.body.getReader();
            const decoder  = new TextDecoder();
            let   fullText = '';
            let   newConvId = activeConvId;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value);
                const lines = chunk.split('\n').filter(l => l.startsWith('data:'));

                for (const line of lines) {
                    try {
                        const data = JSON.parse(line.slice(5).trim());
                        if (data.type === 'delta') {
                            fullText += data.text;
                            setStreamText(fullText);
                        } else if (data.type === 'done') {
                            newConvId = data.conversation_id;
                            fullText  = data.full_text || fullText;
                        } else if (data.type === 'error') {
                            throw new Error(data.message);
                        }
                    } catch (e) {
                        if (e.message !== 'Unexpected end of JSON input') throw e;
                    }
                }
            }

            // Add final AI message
            const aiMsg = {
                role:'assistant', content: fullText,
                timestamp: new Date().toISOString(), id: Date.now() + 1,
            };
            setMessages(prev => [...prev, aiMsg]);
            setStreamText('');

            // Update conversation ID and list
            if (newConvId && newConvId !== activeConvId) {
                setActiveConvId(newConvId);
                // Refresh conversation list
                axiosInstance.get('/ai/conversations')
                    .then(r => setConversations(r.data.data || []))
                    .catch(() => {});
            }

        } catch (err) {
            const errMsg = err.message?.includes('503') || notConfigured
                ? 'AI Assistant is not configured yet. Ask your administrator to set the API key.'
                : err.message?.includes('429')
                ? 'Too many messages. Please wait a moment.'
                : err.message || 'Failed to get AI response. Please try again.';

            setMessages(prev => [...prev, {
                role: 'assistant', content: `⚠️ ${errMsg}`,
                timestamp: new Date().toISOString(), id: Date.now()+1,
                isError: true,
            }]);
            setStreamText('');
        } finally {
            setIsStreaming(false);
            inputRef.current?.focus();
        }
    }, [input, isStreaming, activeConvId, notConfigured]);

    // ── Delete conversation ───────────────────────────────────────────────
    const deleteConversation = async (id, e) => {
        e.stopPropagation();
        try {
            await axiosInstance.delete(`/ai/conversations/${id}`);
            setConversations(prev => prev.filter(c => c.id !== id));
            if (activeConvId === id) newConversation();
        } catch { toast.error('Failed to delete'); }
    };

    // ── Feedback ──────────────────────────────────────────────────────────
    const sendFeedback = async (msgId, rating) => {
        try {
            await axiosInstance.post(`/ai/feedback/${msgId}`, { rating });
            toast.success(rating === 'up' ? 'Thanks for the positive feedback!' : 'Thanks, we\'ll improve!');
        } catch {}
    };

    // ── Key handler ───────────────────────────────────────────────────────
    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <div className="d-flex" style={{ height:'calc(100vh - 70px)', overflow:'hidden' }}>

            {/* ── Sidebar ── */}
            {showSidebar && (
                <div style={{
                    width:280, borderRight:'1px solid #e5e7eb',
                    background:'#fafafa', display:'flex', flexDirection:'column',
                    flexShrink:0,
                }}>
                    {/* Sidebar header */}
                    <div className="p-3 d-flex align-items-center justify-content-between"
                        style={{ borderBottom:'1px solid #e5e7eb' }}>
                        <div className="d-flex align-items-center gap-2">
                            <div style={{ width:28, height:28, borderRadius:8,
                                background:'linear-gradient(135deg,#4f46e5,#7c3aed)',
                                display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <Bot size={16} color="#fff"/>
                            </div>
                            <span className="fw-bold small">PeoplesDock AI</span>
                        </div>
                        <Button size="sm" variant="primary" onClick={newConversation}
                            style={{ padding:'4px 8px' }}>
                            <Plus size={14}/>
                        </Button>
                    </div>

                    {/* Usage badge (HR only) */}
                    {isHR && usage && (
                        <div className="px-3 py-2 small text-muted"
                            style={{ borderBottom:'1px solid #e5e7eb' }}>
                            <div className="d-flex justify-content-between">
                                <span>Conversations (30d)</span>
                                <span className="fw-bold">{usage.total_conversations}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span>Est. cost</span>
                                <span className="fw-bold">${usage.estimated_cost_usd}</span>
                            </div>
                            {!usage.api_configured && (
                                <Alert variant="warning" className="py-1 px-2 mt-1" style={{ fontSize:10 }}>
                                    <Settings size={10} className="me-1"/>API key not set
                                </Alert>
                            )}
                        </div>
                    )}

                    {/* Conversation list */}
                    <div style={{ flex:1, overflowY:'auto' }} className="py-2">
                        {conversations.length === 0 ? (
                            <div className="text-muted text-center p-4 small">
                                No conversations yet
                            </div>
                        ) : (
                            conversations.map(c => (
                                <div key={c.id}
                                    onClick={() => loadConversation(c.id)}
                                    className="d-flex align-items-start px-3 py-2 gap-2"
                                    style={{
                                        cursor:'pointer',
                                        background: activeConvId===c.id ? '#eef2ff' : undefined,
                                        borderLeft: activeConvId===c.id ? '3px solid #4f46e5' : '3px solid transparent',
                                    }}>
                                    <Clock size={13} className="text-muted mt-1 flex-shrink-0"/>
                                    <div style={{ flex:1, minWidth:0 }}>
                                        <div className="small fw-medium text-truncate">
                                            {c.title || 'New conversation'}
                                        </div>
                                        <div className="text-muted" style={{ fontSize:10 }}>
                                            {fmtDate(c.updated_at)} · {c.message_count} msg
                                        </div>
                                    </div>
                                    <Button size="sm" variant="link"
                                        className="p-0 text-muted opacity-0 hover-visible"
                                        style={{ '--bs-btn-color':'#6b7280' }}
                                        onClick={e => deleteConversation(c.id, e)}>
                                        <Trash2 size={12}/>
                                    </Button>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* ── Main chat area ── */}
            <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>

                {/* Chat header */}
                <div className="d-flex align-items-center px-4 py-3 gap-2"
                    style={{ borderBottom:'1px solid #e5e7eb', background:'#fff' }}>
                    <Button variant="link" className="p-0 text-muted me-1"
                        onClick={() => setShowSidebar(p => !p)}>
                        {showSidebar ? <ChevronLeft size={18}/> : <ChevronRight size={18}/>}
                    </Button>
                    <div style={{ width:32, height:32, borderRadius:10,
                        background:'linear-gradient(135deg,#4f46e5,#7c3aed)',
                        display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <Bot size={18} color="#fff"/>
                    </div>
                    <div>
                        <div className="fw-bold small">PeoplesDock AI</div>
                        <div className="text-muted" style={{ fontSize:10 }}>
                            HR Assistant · Powered by I2Speed
                        </div>
                    </div>
                    <Badge bg="success" className="ms-auto" style={{ fontSize:9 }}>
                        ● Online
                    </Badge>
                </div>

                {/* Messages */}
                <div style={{ flex:1, overflowY:'auto', padding:'24px 0' }}
                    className="px-4">

                    {/* Welcome / empty state */}
                    {messages.length === 0 && !isStreaming && (
                        <div className="text-center py-4">
                            <div style={{
                                width:64, height:64, borderRadius:20,
                                background:'linear-gradient(135deg,#4f46e5,#7c3aed)',
                                margin:'0 auto 16px',
                                display:'flex', alignItems:'center', justifyContent:'center',
                            }}>
                                <Bot size={32} color="#fff"/>
                            </div>
                            <h5 className="fw-bold mb-1">
                                Hi {user?.first_name}! I'm your HR Assistant.
                            </h5>
                            <p className="text-muted small mb-4">
                                I can help you with employee data, leave summaries, payroll insights,<br/>
                                draft HR documents, and much more.
                            </p>

                            {/* Quick action chips */}
                            {quickActions.length > 0 && (
                                <div>
                                    <div className="text-muted small mb-3 fw-medium">
                                        Quick actions for you
                                    </div>
                                    <div className="d-flex gap-2 flex-wrap justify-content-center"
                                        style={{ maxWidth:600, margin:'0 auto' }}>
                                        {quickActions.slice(0, 6).map(a => (
                                            <Button key={a.id} variant="outline-primary"
                                                size="sm"
                                                style={{ borderRadius:20, fontSize:12 }}
                                                onClick={() => sendMessage(a.prompt)}>
                                                <Zap size={11} className="me-1"/>
                                                {a.label}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Message list */}
                    {messages.map((msg, i) => (
                        <div key={msg.id || i}
                            className={`d-flex gap-3 mb-4 ${msg.role==='user' ? 'flex-row-reverse' : ''}`}>

                            {/* Avatar */}
                            <div style={{
                                width:32, height:32, borderRadius:10, flexShrink:0,
                                background: msg.role==='user'
                                    ? '#4f46e5'
                                    : 'linear-gradient(135deg,#4f46e5,#7c3aed)',
                                display:'flex', alignItems:'center', justifyContent:'center',
                            }}>
                                {msg.role==='user'
                                    ? <User size={16} color="#fff"/>
                                    : <Bot  size={16} color="#fff"/>}
                            </div>

                            {/* Bubble */}
                            <div style={{ maxWidth:'75%' }}>
                                {/* <div style={{
                                    background:   msg.role==='user' ? '#4f46e5' : '#fff',
                                    color:        msg.role==='user' ? '#fff'    : '#1e293b',
                                    borderRadius: msg.role==='user'
                                        ? '18px 18px 4px 18px'
                                        : '18px 18px 18px 4px',
                                    padding:      '12px 16px',
                                    boxShadow:    '0 1px 3px rgba(0,0,0,0.08)',
                                    fontSize:     14,
                                    lineHeight:   1.6,
                                    border:       msg.isError ? '1px solid #fca5a5' : undefined,
                                    background:   msg.isError ? '#fff1f2'           : undefined,
                                }}>
                                    {msg.role === 'user'
                                        ? <div style={{ whiteSpace:'pre-wrap' }}>{msg.content}</div>
                                        : <MessageContent content={msg.content}/>}
                                </div> */}
                                    <div style={{
                                        background: msg.isError ? '#fff1f2' : msg.role === 'user' ? 'linear-gradient(135deg,#4f46e5,#7c3aed)' : '#fff',
                                        color: msg.role === 'user' ? '#fff' : '#1e293b',
                                        border: msg.isError ? '1px solid #fca5a5' : '1px solid #4f46e5',
                                        borderRadius: msg.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                        padding: '12px 16px',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                        fontSize: 14,
                                        lineHeight: 1.6,
                                    }}
                                    >
                                    {msg.role === 'user' ? (
                                        <div style={{ whiteSpace: 'pre-wrap' }}>
                                        {msg.content}
                                        </div>
                                    ) : (
                                        <MessageContent content={msg.content} />
                                    )}
                                    </div>
                                {/* Meta row */}
                                <div className={`d-flex align-items-center gap-2 mt-1
                                    ${msg.role==='user' ? 'justify-content-end' : ''}`}
                                    style={{ fontSize:10, color:'#9ca3af' }}>
                                    <span>{fmtTime(msg.timestamp)}</span>
                                    {/* Feedback buttons for AI messages */}
                                    {msg.role==='assistant' && !msg.isError && msg.id && (
                                        <div className="d-flex gap-1">
                                            <button
                                                style={{ background:'none', border:'none',
                                                    cursor:'pointer', padding:0, color:'#9ca3af' }}
                                                onClick={() => sendFeedback(msg.id, 'up')}
                                                title="Good response">
                                                <ThumbsUp size={11}/>
                                            </button>
                                            <button
                                                style={{ background:'none', border:'none',
                                                    cursor:'pointer', padding:0, color:'#9ca3af' }}
                                                onClick={() => sendFeedback(msg.id, 'down')}
                                                title="Poor response">
                                                <ThumbsDown size={11}/>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Streaming indicator */}
                    {isStreaming && (
                        <div className="d-flex gap-3 mb-4">
                            <div style={{
                                width:32, height:32, borderRadius:10, flexShrink:0,
                                background:'linear-gradient(135deg,#4f46e5,#7c3aed)',
                                display:'flex', alignItems:'center', justifyContent:'center',
                            }}>
                                <Bot size={16} color="#fff"/>
                            </div>
                            <div style={{
                                background:'#fff', borderRadius:'18px 18px 18px 4px',
                                padding:'12px 16px', boxShadow:'0 1px 3px rgba(0,0,0,0.08)',
                                maxWidth:'75%', fontSize:14, lineHeight:1.6,
                            }}>
                                {streamText
                                    ? <MessageContent content={streamText + ' ▋'}/>
                                    : (
                                        <div className="d-flex gap-1 align-items-center py-1">
                                            {[0,1,2].map(i => (
                                                <div key={i} style={{
                                                    width:8, height:8, borderRadius:'50%',
                                                    background:'#4f46e5', opacity:0.6,
                                                    animation:`bounce 1s ease-in-out ${i*0.2}s infinite`,
                                                }}/>
                                            ))}
                                            <style>{`
                                                @keyframes bounce {
                                                    0%,100%{transform:translateY(0)}
                                                    50%{transform:translateY(-6px)}
                                                }
                                            `}</style>
                                        </div>
                                    )}
                            </div>
                        </div>
                    )}

                    <div ref={bottomRef}/>
                </div>

                {/* ── Input area ── */}
                <div style={{ borderTop:'1px solid #e5e7eb', background:'#fff', padding:'16px 24px' }}>
                    {/* Quick actions row (when there are messages) */}
                    {messages.length > 0 && quickActions.length > 0 && (
                        <div className="d-flex gap-1 flex-wrap mb-3">
                            {quickActions.slice(0, 4).map(a => (
                                <Button key={a.id} variant="light" size="sm"
                                    style={{ borderRadius:16, fontSize:11, padding:'3px 10px',
                                        border:'1px solid #e5e7eb' }}
                                    disabled={isStreaming}
                                    onClick={() => sendMessage(a.prompt)}>
                                    {a.label}
                                </Button>
                            ))}
                        </div>
                    )}

                    <div className="d-flex gap-2 align-items-end">
                        <Form.Control
                            ref={inputRef}
                            as="textarea"
                            rows={1}
                            placeholder="Ask anything about HR, employees, leaves, payroll…"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={onKeyDown}
                            disabled={isStreaming}
                            style={{
                                resize:'none', borderRadius:14,
                                border:'1px solid #e5e7eb',
                                padding:'10px 14px', fontSize:14,
                                maxHeight:120, overflow:'auto',
                                lineHeight:1.5,
                            }}
                        />
                        <Button
                            variant="primary"
                            style={{ borderRadius:12, width:44, height:44, flexShrink:0, padding:0 }}
                            disabled={!input.trim() || isStreaming}
                            onClick={() => sendMessage()}>
                            {isStreaming
                                ? <Spinner size="sm" animation="border"/>
                                : <Send size={18}/>}
                        </Button>
                    </div>
                    <div className="text-muted text-center mt-2" style={{ fontSize:10 }}>
                        I2Speed Cortex AI may make mistakes. Always verify critical HR decisions independently.
                        Press Enter to send · Shift+Enter for new line
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AIAssistant;
